import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productAPI } from '../services/api';

// Query keys for cache management
export const productKeys = {
    all: ['products'],
    lists: () => [...productKeys.all, 'list'],
    list: (filters) => [...productKeys.lists(), filters],
    details: () => [...productKeys.all, 'detail'],
    detail: (id) => [...productKeys.details(), id],
    popular: () => [...productKeys.all, 'popular'],
};

// Fetch all products with optional filters
export const useProducts = (params = {}) => {
    return useQuery({
        queryKey: productKeys.list(params),
        queryFn: async () => {
            const response = await productAPI.getAll(params);
            return response.data;
        },
    });
};

// Fetch single product by ID
export const useProduct = (id) => {
    return useQuery({
        queryKey: productKeys.detail(id),
        queryFn: async () => {
            const response = await productAPI.getById(id);
            return response.data;
        },
        enabled: !!id, // Only run query if id exists
    });
};

// Fetch popular products
export const usePopularProducts = () => {
    return useQuery({
        queryKey: productKeys.popular(),
        queryFn: async () => {
            const response = await productAPI.getPopular();
            return response.data;
        },
    });
};

// Create product mutation
export const useCreateProduct = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data) => productAPI.create(data),
        onSuccess: () => {
            // Invalidate and refetch products list
            queryClient.invalidateQueries({ queryKey: productKeys.lists() });
        },
    });
};

// Update product mutation
export const useUpdateProduct = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }) => productAPI.update(id, data),
        onSuccess: (_, variables) => {
            // Invalidate specific product and lists
            queryClient.invalidateQueries({ queryKey: productKeys.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: productKeys.lists() });
        },
    });
};

// Delete product mutation
export const useDeleteProduct = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id) => productAPI.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: productKeys.lists() });
        },
    });
};

// Toggle product status mutation
export const useToggleProductStatus = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id) => productAPI.toggleStatus(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: productKeys.detail(id) });
            queryClient.invalidateQueries({ queryKey: productKeys.lists() });
        },
    });
};

// Toggle product popular status mutation
export const useToggleProductPopular = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id) => productAPI.togglePopular(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: productKeys.popular() });
            queryClient.invalidateQueries({ queryKey: productKeys.lists() });
        },
    });
};
